import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro";
import {
  DeleteIcon,
  DownloadIcon,
  // DownloadIcon,
  ErrorfilledIcon,
  GKButton,
  GKDataGrid,
  Grid2,
  IconButton,
  NoData,
  PlusIcon,
  PopConfirm,
  SackOfMoneyIcon,
  Show,
  StatusField,
  Typography,
} from "components";
import useGenerateQueryParams from "hooks/useGenerateQueryParams";
import { CalculationRowData } from "interfaces/Revenue";
import ImportModal from "Pages/RevenueManagement/FeeCollection/ImportModal";
import { useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import {
  useDeleteFeeCollectionMutation,
  useGetFeeCollectionListQuery,
} from "store/features/Revenue/ProformaApi";
import { useAppSelector } from "store/store";
import { paletteLightTheme } from "utils/ColorTheme";
import { handleCapitalize, UserRoleTypes } from "utils/common";
import { extraColumnsField, matchDate } from "utils/helpers";
import { moneyCommaSeparator } from "utils/MathFunction";

interface Props {
  name?: string;
  portfolioId?: string;
  tableName?: string;
  clientId?: string;
}

const FeeCollection = ({ name, portfolioId, tableName, clientId }: Props) => {
  const userData = useAppSelector((state) => state?.user?.userData);
  const navigate = useNavigate();

  const [feeCollectionId, setFeeCollectionId] = useState(null);
  const [confirmationModal, setConfirmationModal] =
    useState<HTMLElement | null>(null);
  const [openImportModal, setOpenImportModal] = useState(false);

  const requestBody = useGenerateQueryParams(name || "feeCollection", {
    portfolio_id: portfolioId,
  });

  const { data, isFetching } = useGetFeeCollectionListQuery(requestBody);
  const [deleteFeeCollection, { isLoading }] = useDeleteFeeCollectionMutation();

  const handleDeleteCollection = (
    e: React.MouseEvent<HTMLElement>,
    id: string,
  ) => {
    e.stopPropagation();
    setFeeCollectionId(id);
    setConfirmationModal(e.currentTarget);
  };

  const handleNavigate = (path: string) => navigate(path);

  const columns: GridColDef[] = [
    {
      ...extraColumnsField(
        `Client Name (${data?.totalCount || 0})`,
        "clientName",
        250,
        1,
      ),
    },
    {
      ...extraColumnsField("Date", "date", 100, 1),
      valueFormatter: (value) => matchDate(value.value),
    },
    {
      ...extraColumnsField("Amount (₹)", "amount", 150, 1),
      valueGetter: (row) => moneyCommaSeparator(row?.row?.amount),
      type: "number",
    },
    {
      ...extraColumnsField("Payment Type", "paymentType", 150, 1),
      valueGetter: (row) => handleCapitalize(row?.row?.paymentType),
    },
    {
      ...extraColumnsField("Payment Status", "status", 150, 1),
      renderCell: (row) => <StatusField statusString={row?.row?.status} />,
    },
    ...(userData?.userRole === UserRoleTypes.advisor
      ? [
          {
            ...extraColumnsField("Actions", "action", 100, 1),
            type: "action",
            renderCell: ({ row }: GridRenderCellParams<CalculationRowData>) => {
              return (
                <IconButton
                  className="outline"
                  color="error"
                  disabled={row.id === feeCollectionId && isLoading}
                  onClick={(event) => handleDeleteCollection(event, row.id)}
                >
                  <DeleteIcon />
                </IconButton>
              );
            },
          },
        ]
      : []),
  ];

  const showMigratedToast = () =>
    toast(() => (
      <span
        style={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          gap: 12,
        }}
      >
        <ErrorfilledIcon
          width={24}
          color={paletteLightTheme.palette.warning.main}
        />
        <Typography>
          This fee collection cannot be viewed because it is historically
          migrated
        </Typography>
      </span>
    ));

  return (
    <>
      <GKDataGrid
        loading={isFetching}
        columns={columns}
        rows={data?.data || []}
        headerComponent={
          <Show.When isTrue={userData?.userRole === UserRoleTypes.advisor}>
            <Grid2
              container
              display={"flex"}
              alignItems={"center"}
              justifyContent={"flex-end"}
              gap={1}
            >
              <GKButton
                variant="contained"
                onClick={() => {
                  handleNavigate(
                    name
                      ? `/app/revenue/fee-collection/?portfolio_id=${portfolioId}&client_id=${clientId}`
                      : "/app/revenue/fee-collection/",
                  );
                }}
                startIcon={<PlusIcon width={16} />}
              >
                Add Fee Collection
              </GKButton>
              <GKButton
                variant="contained"
                onClick={() => setOpenImportModal(true)}
                startIcon={<DownloadIcon width={16} height={16} />}
              >
                Import Fees
              </GKButton>
            </Grid2>
          </Show.When>
        }
        onRowClick={({ row }) =>
          row.isHistoricallyMigrated
            ? showMigratedToast()
            : handleNavigate(`/app/revenue/fee-collection/${row.id}/`)
        }
        maxHeight={"calc(100vh - 306px)"}
        totalCount={data?.totalCount ?? 0}
        name={name ?? "feeCollection"}
        tableName={tableName ? "" : "fee_collection"}
        emptyComponent={
          <NoData
            text="No Fee Collections Yet"
            subText="Start collecting fees by creating a new collection entry for your clients."
            customImg={
              <SackOfMoneyIcon
                height={80}
                width={80}
                color="var(--neutral-100)"
              />
            }
          />
        }
      />

      <Show.When isTrue={Boolean(confirmationModal)}>
        <PopConfirm
          anchorEl={confirmationModal}
          open={Boolean(confirmationModal)}
          setOpen={setConfirmationModal}
          onSuccess={async () => {
            try {
              await deleteFeeCollection(feeCollectionId).unwrap();
            } catch (err) {
              console.error("Delete fee collection failed:", err);
            }
          }}
        />
      </Show.When>

      <Show.When isTrue={openImportModal}>
        <ImportModal open={openImportModal} setOpen={setOpenImportModal} />
      </Show.When>
    </>
  );
};

export default FeeCollection;
